// MfaEnrollmentWizard — mockup screen 4 (Settings → Security → Set up two-factor
// authentication). A single modal that advances step-by-step:
//   1. Choose a method (SMS/email disabled until MFA_METHODS enables them)
//   2. Connect & verify   (TOTP: associate → QR + manual key → verify;
//                          SMS: verify the phone via /users/me/attributes;
//                          Email: enabled directly — the account email is verified)
//   3. Save recovery codes (generated once, download/copy + acknowledgement gate)
//
// All MFA state-changing calls route through the D14 server endpoints in
// services/api.ts (never raw Cognito), so the security-notification email fires
// server-side. iflow palette only; never a dark theme.
import { useEffect, useMemo, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Loader2, X, ShieldCheck, Smartphone, MessageSquare, Mail, Download, Copy, Check } from 'lucide-react'
import {
  associateMfa,
  verifyMfa,
  setMfaPreference,
  changeMfaAttribute,
  regenerateRecoveryCodes,
  type MfaMethodToken,
  type MfaAssociateResponse,
} from '../../services/api'

interface MfaRuntimeConfig {
  MFA_METHODS?: string
  MFA_REMEMBER_DEVICE?: boolean
}
function mfaMethodsEnabled(): string[] {
  const cfg = (typeof window !== 'undefined'
    ? (window.INNOVATEFLOW_CONFIG as unknown as MfaRuntimeConfig)
    : null)
  const raw = cfg?.MFA_METHODS ?? 'totp'
  return raw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
}

/** Group a base32 secret into readable 4-character blocks (mockup screen 4). */
function groupInFours(secret: string): string {
  return secret.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
}

type Method = MfaMethodToken

interface WizardProps {
  open: boolean
  onClose: () => void
  /** Called after a successful finish so the parent can refetch + toast. */
  onEnrolled: () => void
  /** true when the account already holds recovery codes — step 3 then does NOT
   *  regenerate (which would invalidate the user's saved codes) and simply
   *  confirms. */
  hasRecoveryCodes: boolean
  /** true when the account already has an active (preferred) MFA method — an
   *  attribute change (SMS enrol) then requires a current MFA code (D14/R20). */
  hasActiveMfa: boolean
}

const METHOD_META: Record<Method, { label: string; icon: typeof Smartphone; blurb: string }> = {
  SOFTWARE_TOKEN_MFA: {
    label: 'Authenticator app',
    icon: Smartphone,
    blurb: 'Codes from Google Authenticator, Microsoft Authenticator, 1Password or any TOTP app. Works offline.',
  },
  SMS_MFA: {
    label: 'Text message (SMS)',
    icon: MessageSquare,
    blurb: 'A 6-digit code sent to your mobile. Requires a verified Australian mobile number.',
  },
  EMAIL_OTP: {
    label: 'Email',
    icon: Mail,
    blurb: 'A 6-digit code sent to your verified email address.',
  },
}

export default function MfaEnrollmentWizard({ open, onClose, onEnrolled, hasRecoveryCodes, hasActiveMfa }: WizardProps) {
  const enabled = useMemo(() => mfaMethodsEnabled(), [])
  const smsEnabled = enabled.includes('sms')
  const emailEnabled = enabled.includes('email')

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [method, setMethod] = useState<Method>('SOFTWARE_TOKEN_MFA')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Step-2 (TOTP)
  const [assoc, setAssoc] = useState<MfaAssociateResponse | null>(null)
  const [code, setCode] = useState('')
  // Step-2 (SMS)
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [currentCode, setCurrentCode] = useState('')
  // Step-3 (recovery)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [ack, setAck] = useState(false)
  const [copied, setCopied] = useState(false)

  // Reset all transient state whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setStep(1)
      setMethod('SOFTWARE_TOKEN_MFA')
      setBusy(false)
      setError('')
      setAssoc(null)
      setCode('')
      setPhone('')
      setPassword('')
      setCurrentCode('')
      setRecoveryCodes(null)
      setAck(false)
      setCopied(false)
    }
  }, [open])

  if (!open) return null

  // ---- Step transitions ----------------------------------------------------

  async function goToVerify() {
    setError('')
    if (method === 'SOFTWARE_TOKEN_MFA') {
      setBusy(true)
      try {
        const res = await associateMfa()
        setAssoc(res)
        setStep(2)
      } catch (e) {
        setError(errMsg(e, 'Could not start authenticator setup.'))
      } finally {
        setBusy(false)
      }
      return
    }
    // SMS / email collect their inputs on step 2 — no pre-call needed.
    setStep(2)
  }

  async function enterRecoveryStep() {
    // Enrolment succeeded — now surface (or confirm) recovery codes.
    if (hasRecoveryCodes) {
      setStep(3)
      return
    }
    setBusy(true)
    try {
      const res = await regenerateRecoveryCodes()
      setRecoveryCodes(res.codes)
      setStep(3)
    } catch (e) {
      setError(errMsg(e, 'Enabled, but could not generate recovery codes. You can generate them from Security settings.'))
      // Still advance — MFA is on; recovery codes can be generated later.
      setStep(3)
    } finally {
      setBusy(false)
    }
  }

  async function verifyAndEnable() {
    setError('')
    setBusy(true)
    try {
      if (method === 'SOFTWARE_TOKEN_MFA') {
        if (!/^\d{6}$/.test(code.trim())) throw new Error('Enter the 6-digit code from your app.')
        await verifyMfa(code.trim(), 'Authenticator app')
        await setMfaPreference('SOFTWARE_TOKEN_MFA')
      } else if (method === 'SMS_MFA') {
        if (!phone.trim()) throw new Error('Enter your mobile number.')
        if (!password) throw new Error('Enter your current password to confirm this change.')
        if (hasActiveMfa && !currentCode.trim()) throw new Error('Enter a code from your current method to confirm.')
        await changeMfaAttribute('phone_number', phone.trim(), password, hasActiveMfa ? currentCode.trim() : undefined)
        await setMfaPreference('SMS_MFA')
      } else {
        // EMAIL_OTP — the account email is already verified; enable directly.
        await setMfaPreference('EMAIL_OTP')
      }
      await enterRecoveryStep()
    } catch (e) {
      setError(errMsg(e, 'Verification failed. Check the details and try again.'))
    } finally {
      setBusy(false)
    }
  }

  function finish() {
    onEnrolled()
    onClose()
  }

  function downloadCodes() {
    if (!recoveryCodes) return
    const blob = new Blob([`InnovateFlow recovery codes\n\n${recoveryCodes.join('\n')}\n`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'innovateflow-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  async function copyCodes() {
    if (!recoveryCodes) return
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked — the download button remains available */
    }
  }

  // ---- Render --------------------------------------------------------------

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Set up two-factor authentication">
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => !busy && onClose()} aria-hidden />
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-iflow-50 text-iflow-600"><ShieldCheck size={18} /></span>
            <h2 className="text-lg font-bold text-iflow-900">Set up two-factor authentication</h2>
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="text-slate-400 hover:text-slate-600" disabled={busy}>
            <X size={18} />
          </button>
        </div>

        <StepDots step={step} />

        {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

        {/* STEP 1 — choose a method */}
        {step === 1 && (
          <div>
            <h3 className="text-base font-bold text-iflow-900 mb-3">How do you want to verify sign-ins?</h3>
            <div className="space-y-3">
              {(['SOFTWARE_TOKEN_MFA', 'SMS_MFA', 'EMAIL_OTP'] as Method[]).map((m) => {
                const meta = METHOD_META[m]
                const Icon = meta.icon
                const disabled = (m === 'SMS_MFA' && !smsEnabled) || (m === 'EMAIL_OTP' && !emailEnabled)
                const selected = method === m
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={disabled}
                    onClick={() => setMethod(m)}
                    className={`w-full text-left rounded-xl border p-4 transition-colors ${
                      disabled
                        ? 'border-slate-200 opacity-50 cursor-not-allowed'
                        : selected
                          ? 'border-iflow-500 bg-iflow-50 ring-2 ring-iflow-200'
                          : 'border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon size={22} className="text-iflow-600 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-iflow-900 text-sm">{meta.label}</span>
                          {m === 'SOFTWARE_TOKEN_MFA' && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-500/20 text-green-700">Recommended</span>
                          )}
                          {disabled && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Coming soon</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{meta.blurb}</p>
                      </div>
                      <span className={`w-4 h-4 rounded-full border mt-0.5 shrink-0 ${selected ? 'border-[5px] border-iflow-600 bg-white' : 'border-slate-300 bg-white'}`} />
                    </div>
                  </button>
                )
              })}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
              <button type="button" onClick={goToVerify} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-semibold text-white hover:bg-iflow-700 disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} Continue
              </button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-2">You can add more methods later as backups.</p>
          </div>
        )}

        {/* STEP 2 — connect & verify */}
        {step === 2 && (
          <div>
            {method === 'SOFTWARE_TOKEN_MFA' && (
              <div>
                <h3 className="text-base font-bold text-iflow-900 mb-1">Scan with your authenticator app</h3>
                <p className="text-xs text-slate-500 mb-4">Then enter the 6-digit code it shows to confirm the pairing.</p>
                <div className="flex justify-center mb-3">
                  <div className="p-3 bg-white border border-slate-200 rounded-xl">
                    {assoc?.otpauthUri
                      ? <QRCodeSVG value={assoc.otpauthUri} size={148} level="M" />
                      : <div className="w-[148px] h-[148px] flex items-center justify-center text-slate-300"><Loader2 className="animate-spin" /></div>}
                  </div>
                </div>
                {assoc?.secretCode && (
                  <div className="text-center mb-4">
                    <p className="text-xs text-slate-400">Can&apos;t scan? Enter this key manually:</p>
                    <code className="text-xs font-mono text-iflow-800 bg-iflow-50 border border-iflow-100 rounded px-2 py-1 inline-block mt-1 tracking-wider">
                      {groupInFours(assoc.secretCode)}
                    </code>
                  </div>
                )}
                <label htmlFor="mfa-totp-code" className="block text-sm font-semibold text-iflow-900 mb-1">Verification code</label>
                <input
                  id="mfa-totp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 text-center tracking-[0.35em] font-mono"
                  placeholder="000000"
                />
              </div>
            )}

            {method === 'SMS_MFA' && (
              <div className="space-y-3">
                <h3 className="text-base font-bold text-iflow-900 mb-1">Verify your mobile number</h3>
                <p className="text-xs text-slate-500">We&apos;ll send a test code to confirm the number and enable SMS codes.</p>
                <div>
                  <label htmlFor="mfa-phone" className="block text-sm font-semibold text-iflow-900 mb-1">Mobile number</label>
                  <input id="mfa-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61 4XX XXX XXX" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" />
                </div>
                <div>
                  <label htmlFor="mfa-pw" className="block text-sm font-semibold text-iflow-900 mb-1">Current password</label>
                  <input id="mfa-pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" />
                </div>
                {hasActiveMfa && (
                  <div>
                    <label htmlFor="mfa-current" className="block text-sm font-semibold text-iflow-900 mb-1">Code from your current method</label>
                    <input id="mfa-current" inputMode="numeric" autoComplete="one-time-code" value={currentCode} onChange={(e) => setCurrentCode(e.target.value.replace(/\D/g, ''))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" placeholder="000000" />
                  </div>
                )}
              </div>
            )}

            {method === 'EMAIL_OTP' && (
              <div>
                <h3 className="text-base font-bold text-iflow-900 mb-1">Enable email codes</h3>
                <p className="text-sm text-slate-600 mb-3">Your account email is already verified, so email codes can be enabled directly — no extra step needed.</p>
                <div className="rounded-lg bg-sky-500/5 border border-sky-200/60 p-3 text-xs text-slate-600">
                  We&apos;ll send a 6-digit code to your account email each time you sign in and choose this method.
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-between">
              <button type="button" onClick={() => setStep(1)} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Back</button>
              <button type="button" onClick={verifyAndEnable} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-semibold text-white hover:bg-iflow-700 disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />}
                {method === 'EMAIL_OTP' ? 'Enable' : 'Verify & enable'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — recovery codes */}
        {step === 3 && (
          <div>
            <h3 className="text-base font-bold text-iflow-900 mb-1">
              {hasRecoveryCodes ? "You're all set" : 'Store these somewhere safe'}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              {hasRecoveryCodes
                ? 'Two-factor authentication is enabled for this method. Your existing recovery codes still work.'
                : 'Each code signs you in once if you lose access to your other methods.'}
            </p>

            {!hasRecoveryCodes && recoveryCodes && (
              <>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-xl p-4 mb-3">
                  {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
                </div>
                <div className="flex gap-2 mb-4">
                  <button type="button" onClick={downloadCodes} className="flex-1 inline-flex items-center justify-center gap-1 py-2 border border-iflow-300 text-iflow-700 text-sm font-semibold rounded-lg hover:bg-iflow-50"><Download size={14} /> Download .txt</button>
                  <button type="button" onClick={copyCodes} className="flex-1 inline-flex items-center justify-center gap-1 py-2 border border-iflow-300 text-iflow-700 text-sm font-semibold rounded-lg hover:bg-iflow-50">
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700 mb-4">
                  <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="rounded border-slate-300 text-iflow-600 mt-0.5" />
                  I&apos;ve saved these codes. I understand each works once and they&apos;re my only backup if I lose my device.
                </label>
              </>
            )}

            <button
              type="button"
              onClick={finish}
              disabled={!hasRecoveryCodes && !!recoveryCodes && !ack}
              className="w-full py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Finish setup
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function StepDots({ step }: { step: 1 | 2 | 3 }) {
  const labels = ['Choose a method', 'Connect & verify', 'Save recovery codes']
  return (
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-4">
      {labels.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3
        const active = n === step
        const done = n < step
        return (
          <div key={label} className="flex items-center gap-1.5">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${active || done ? 'bg-iflow-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{n}</span>
            <span className={active ? 'text-iflow-700' : 'text-slate-300'}>{active ? label : ''}</span>
            {n < 3 && <span className="text-slate-300">·</span>}
          </div>
        )
      })}
    </div>
  )
}

function errMsg(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string; error?: string } } }).response
    const m = resp?.data?.message || resp?.data?.error
    if (m) return m
  }
  return e instanceof Error ? e.message : fallback
}
